/**
 * useMatterLink - the research session's matter link.
 *
 * The relation already existed (`research_chat_groups.matter_id`/`.matter_uuid`)
 * but was only settable at chat creation, which no UI ever did. These endpoints
 * make it editable:
 *
 *   GET    research/chat-groups/{uuid}/matter  → { matter: {...} | null }
 *   PUT    …/matter  { matter_uuid }           → link
 *   DELETE …/matter                            → unlink
 *
 * Linking is not cosmetic: the backend puts `matter_uuid` on every query it
 * queues, and the engine folds that matter's documents and extracted context
 * into the grounding set for each turn. So the link changes answers, and the
 * UI has to be honest about when it is and isn't in force.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../store';
import {
  ResearchLinkedMatter,
  researchGetMatterLink,
  researchSetMatterLink,
} from '../../store/actions/ChatAction';

export interface UseMatterLinkResult {
  matter: ResearchLinkedMatter | null;
  isLoading: boolean;
  isSaving: boolean;
  link: (matterUuid: string) => Promise<void>;
  unlink: () => Promise<void>;
}

export function useMatterLink(chatUuid: string | null): UseMatterLinkResult {
  const dispatch = useAppDispatch();
  const [matter, setMatter] = useState<ResearchLinkedMatter | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Guards a response arriving for a chat the user has already left.
  const activeUuidRef = useRef<string | null>(null);
  activeUuidRef.current = chatUuid;

  useEffect(() => {
    if (!chatUuid) {
      setMatter(null);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    dispatch(researchGetMatterLink(chatUuid))
      .then((result) => {
        if (cancelled || activeUuidRef.current !== chatUuid) return;
        setMatter(result);
      })
      .catch(() => {
        // A failed read must not imply "unlinked" - that would tell the user
        // the matter is off when the backend is still applying it. Leave the
        // last known state; the error toast already fired.
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [chatUuid, dispatch]);

  const apply = useCallback(
    async (matterUuid: string | null) => {
      const uuid = activeUuidRef.current;
      if (!uuid) return;
      setIsSaving(true);
      try {
        // Adopt the server's payload rather than the picked row: it carries the
        // canonical title and matter number, and confirms the write landed.
        const result = await dispatch(researchSetMatterLink(uuid, matterUuid));
        if (activeUuidRef.current === uuid) setMatter(result);
      } catch {
        // handleApiError surfaced it; keep the last known good state.
      } finally {
        setIsSaving(false);
      }
    },
    [dispatch]
  );

  return {
    matter,
    isLoading,
    isSaving,
    link: (matterUuid: string) => apply(matterUuid),
    unlink: () => apply(null),
  };
}

export default useMatterLink;
